import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface QueueHealth {
  readonly pending: number;
  readonly processing: number;
  readonly completed: number;
  readonly failed: number;
  readonly dlq: number;
  readonly backlog: number;
}
interface MessageRow {
  readonly messageId: string;
  readonly queueName: string;
  readonly messageType: string;
  readonly priority: string;
  readonly status: string;
  readonly retryCount: number;
  readonly maxRetries: number;
}
interface DlqRow {
  readonly dlqId: string;
  readonly dlqCode: string;
  readonly queueName: string;
  readonly messageType: string;
  readonly referenceCode: string | null;
  readonly failureReason: string;
  readonly replayCount: number;
  readonly status: string;
}

/** M29 — Queue Monitoring (§33 / §20-§22). Live message-queue health (pending,
 * processing, completed, failed, dead-letter, backlog) with the most recent
 * messages and their retry counters, plus dead-letter-queue triage — replay,
 * discard and resolve — each wired to the guarded /v1/eip/dlq write API
 * (BR-05 retry / dead-letter handling). This screen is the intended
 * /admin/integration/dlq remediation console. */
export function M29QueueMonitoringPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [health, setHealth] = useState<QueueHealth | null>(null);
  const [rows, setRows] = useState<MessageRow[]>([]);
  const [dlqRows, setDlqRows] = useState<DlqRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // remediation
  const [discardReason, setDiscardReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: QueueHealth }>('/v1/eip/monitoring/queue', { token }),
      apiRequest<{ data: MessageRow[] }>('/v1/eip/queue/messages', { token }),
      apiRequest<{ data: DlqRow[] }>('/v1/eip/dlq', { token }),
    ])
      .then(([h, m, d]) => { if (alive) { setHealth(h.data); setRows(m.data); setDlqRows(d.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m29.queue.notAuthorised', { verb, perm })
      : (err as Error).message;

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const replay = (d: DlqRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/dlq/${d.dlqId}/replay`, { method: 'PUT', body: {}, token }),
      t('m29.queue.noticeReplayed', { code: d.dlqCode }), t('m29.queue.verbReplay'), 'M29_DLQ_MANAGE',
    );

  const discard = (d: DlqRow): Promise<void> => {
    if (!discardReason.trim()) { setActionError(t('m29.queue.validationDiscardReason')); return Promise.resolve(); }
    return runRowAction(
      () => apiRequest(`/v1/eip/dlq/${d.dlqId}/discard`, { method: 'PUT', body: { reason: discardReason.trim() }, token }),
      t('m29.queue.noticeDiscarded', { code: d.dlqCode }), t('m29.queue.verbDiscard'), 'M29_DLQ_MANAGE',
    ).then(() => setDiscardReason(''));
  };

  const resolve = (d: DlqRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/dlq/${d.dlqId}/resolve`, { method: 'PUT', body: {}, token }),
      t('m29.queue.noticeResolved', { code: d.dlqCode }), t('m29.queue.verbResolve'), 'M29_DLQ_MANAGE',
    );

  return (
    <main aria-label="queue-monitoring">
      <h1>{t('m29.queue.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !health ? <p>{t('m29.queue.loading')}</p> : (
        <>
          <section aria-label="queue-health" className="card-grid">
            <article><h2>{t('m29.queue.cardPending')}</h2><p aria-label="q-pending">{health.pending}</p></article>
            <article><h2>{t('m29.queue.cardProcessing')}</h2><p aria-label="q-processing">{health.processing}</p></article>
            <article><h2>{t('m29.queue.cardCompleted')}</h2><p aria-label="q-completed">{health.completed}</p></article>
            <article><h2>{t('m29.queue.cardFailed')}</h2><p aria-label="q-failed">{health.failed}</p></article>
            <article><h2>{t('m29.queue.cardDeadLetter')}</h2><p aria-label="q-dlq">{health.dlq}</p></article>
            <article><h2>{t('m29.queue.cardBacklog')}</h2><p aria-label="q-backlog">{health.backlog}</p></article>
          </section>
          <section aria-label="queue-messages">
            <h2>{t('m29.queue.recentHeading')}</h2>
            <p aria-label="message-count">{t('m29.queue.messageCount', { count: rows.length })}</p>
            <table aria-label="message-table">
              <thead><tr><th>{t('m29.queue.mColQueue')}</th><th>{t('m29.queue.mColType')}</th><th>{t('m29.queue.mColPriority')}</th><th>{t('m29.queue.mColStatus')}</th><th>{t('m29.queue.mColRetries')}</th></tr></thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.messageId}>
                    <td>{m.queueName}</td><td>{m.messageType}</td><td>{m.priority}</td>
                    <td>{m.status}</td><td>{m.retryCount}/{m.maxRetries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <section aria-label="dlq-remediation">
            <h2>{t('m29.queue.dlqHeading')}</h2>
            <p aria-label="dlq-count">{t('m29.queue.dlqCount', { count: dlqRows.length })}</p>
            <label>{t('m29.queue.fieldDiscardReason')}<input aria-label="field-discard-reason" value={discardReason} onChange={(e) => setDiscardReason(e.target.value)} /></label>
            {actionError && <p role="alert" aria-label="dlq-error">{actionError}</p>}
            {notice && <p aria-label="dlq-notice">{notice}</p>}
            <table aria-label="dlq-table">
              <thead><tr><th>{t('m29.queue.dColCode')}</th><th>{t('m29.queue.dColQueue')}</th><th>{t('m29.queue.dColType')}</th><th>{t('m29.queue.dColReference')}</th><th>{t('m29.queue.dColReason')}</th><th>{t('m29.queue.dColReplays')}</th><th>{t('m29.queue.dColStatus')}</th><th>{t('m29.queue.dColActions')}</th></tr></thead>
              <tbody>
                {dlqRows.length === 0 ? (
                  <tr><td colSpan={8}>{t('m29.queue.noDlq')}</td></tr>
                ) : dlqRows.map((d) => (
                  <tr key={d.dlqId}>
                    <td>{d.dlqCode}</td><td>{d.queueName}</td><td>{d.messageType}</td>
                    <td>{d.referenceCode ?? '—'}</td><td>{d.failureReason}</td><td>{d.replayCount}</td><td>{d.status}</td>
                    <td>
                      <button type="button" aria-label={`replay-${d.dlqCode}`} onClick={() => void replay(d)}>{t('m29.queue.btnReplay')}</button>
                      <button type="button" aria-label={`discard-${d.dlqCode}`} onClick={() => void discard(d)}>{t('m29.queue.btnDiscard')}</button>
                      <button type="button" aria-label={`resolve-${d.dlqCode}`} onClick={() => void resolve(d)}>{t('m29.queue.btnResolve')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </main>
  );
}
